import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tag, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Promotions() {
  const [promotions, setPromotions] = useState([]);
  const [promoProducts, setPromoProducts] = useState([]);
  const [promoLots, setPromoLots] = useState([]);
  const [noPromoMessage, setNoPromoMessage] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [promosRes, productsRes, lotsRes, contentRes] = await Promise.all([
          axios.get(`${API}/promotions?active_only=true`),
          axios.get(`${API}/products`),
          axios.get(`${API}/lots`),
          axios.get(`${API}/content`),
        ]);
        
        setPromotions(promosRes.data);
        setPromoProducts(productsRes.data.filter(p => p.is_promotion));
        setPromoLots(lotsRes.data.filter(l => l.is_promotion));
        
        const noPromoContent = contentRes.data.find(c => c.key === "no_promo_message");
        setNoPromoMessage(noPromoContent?.value || "Pas de promotion en ce moment. Revenez bientôt pour découvrir nos offres spéciales !");
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const hasPromotions = promotions.length > 0 || promoProducts.length > 0 || promoLots.length > 0;

  return (
    <div className="py-12 md:py-16" data-testid="promotions-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 flex items-center justify-center gap-3">
            <Tag className="h-10 w-10 text-accent" />
            Promotions
          </h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Découvrez nos offres spéciales et faites des économies sur vos produits préférés.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="aspect-video" />
                <CardContent className="p-6">
                  <Skeleton className="h-7 w-3/4 mb-3" />
                  <Skeleton className="h-4 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : !hasPromotions ? (
          <div className="text-center py-16 bg-muted/30 rounded-2xl">
            <Sparkles className="h-16 w-16 text-accent/50 mx-auto mb-6" />
            <p className="text-xl text-gray-600 max-w-md mx-auto" data-testid="no-promo-message">
              {noPromoMessage}
            </p>
          </div>
        ) : (
          <div className="space-y-12">
            {/* Active Promotions */}
            {promotions.length > 0 && (
              <section>
                <h2 className="font-heading text-2xl font-semibold text-gray-900 mb-6">
                  Offres en cours
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {promotions.map((promo) => (
                    <Card
                      key={promo.id}
                      className="overflow-hidden border-accent/20 bg-accent/5"
                      data-testid={`promotion-${promo.id}`}
                    >
                      {promo.image_url && (
                        <div className="aspect-video overflow-hidden">
                          <img
                            src={promo.image_url}
                            alt={promo.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-heading text-xl font-semibold text-gray-900">
                              {promo.title}
                            </h3>
                            <p className="text-gray-600 mt-2">{promo.description}</p>
                          </div>
                          {promo.discount && (
                            <span className="bg-accent text-white text-sm font-bold px-3 py-1 rounded flex-shrink-0">
                              {promo.discount}
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            )}

            {/* Products on Promotion */}
            {promoProducts.length > 0 && (
              <section>
                <h2 className="font-heading text-2xl font-semibold text-gray-900 mb-6">
                  Produits en promotion
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {promoProducts.map((product) => (
                    <Card
                      key={product.id}
                      className="overflow-hidden card-hover relative"
                      data-testid={`promo-product-${product.id}`}
                    >
                      <span className="promo-badge">Promo</span>
                      <div className="aspect-square overflow-hidden bg-gray-100">
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-heading text-lg font-semibold text-gray-900">
                          {product.name}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {product.description}
                        </p>
                        {product.price && (
                          <div className="mt-3 flex items-center gap-2">
                            <span className="text-lg font-bold text-accent">
                              {product.promotion_price || product.price}
                            </span>
                            {product.promotion_price && (
                              <span className="text-sm text-gray-400 line-through">
                                {product.price}
                              </span>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            )}

            {/* Lots on Promotion */}
            {promoLots.length > 0 && (
              <section>
                <h2 className="font-heading text-2xl font-semibold text-gray-900 mb-6">
                  Lots en promotion
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {promoLots.map((lot) => (
                    <Card
                      key={lot.id}
                      className="overflow-hidden card-hover relative"
                      data-testid={`promo-lot-${lot.id}`}
                    >
                      <span className="promo-badge">Promo</span>
                      <div className="aspect-video overflow-hidden bg-gray-100">
                        <img
                          src={lot.image_url}
                          alt={lot.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <CardContent className="p-6">
                        <h3 className="font-heading text-xl font-semibold text-gray-900">
                          {lot.name}
                        </h3>
                        <p className="text-gray-600 mt-2">{lot.description}</p>
                        <div className="mt-4 bg-muted/50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 whitespace-pre-line">
                            {lot.contents}
                          </p>
                        </div>
                        {lot.price && (
                          <div className="mt-4 flex items-center gap-2">
                            <span className="text-2xl font-bold text-accent">
                              {lot.promotion_price || lot.price}
                            </span>
                            {lot.promotion_price && (
                              <span className="text-lg text-gray-400 line-through">
                                {lot.price}
                              </span>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
